import { useStaticQuery, graphql } from 'gatsby'
import { uniqueByKey } from '@utils'
import pageOptions from '../../config/page-options.js'

const SEO = ({
  description = '',
  lang = 'de',
  image: metaImage,
  title,
  pathname,
  canonical,
  fallbackImage = null,
  noindex = false
}) => {
  const data = useStaticQuery(graphql`
    query {
      allStrapiGeneral {
        nodes {
          locale
          siteMetadata {
            id
            siteTitle
            siteUrl
            canonical
            siteDescription
            siteLanguage
            siteLogo {
              ...StrapiMedia
            }
            contact {
              contactType
              email
              telephone
              streetAddress
              addressLocality
              postalCode
              addressCountry
            }
            socials {
              link
            }
            twitterUsername
          }
        }
      }
    }
  `)

  // useStaticQuery cannot take variables, so every locale is sourced and the
  // matching one is picked at render time -- the same pattern the header and
  // footer layouts already use. With i18n.locale='*' a singular `strapiGeneral`
  // would resolve to whichever localization the node store happened to return.
  const generalNodes = data?.allStrapiGeneral?.nodes || []
  const siteMetadata = (
    generalNodes.find((n) => n.locale === lang) ||
    generalNodes.find((n) => n.locale === pageOptions.defaultLocale)
  )?.siteMetadata
  if (!siteMetadata) {
    return null
  }

  if (
    !siteMetadata.siteTitle ||
    !siteMetadata.siteUrl ||
    !siteMetadata.siteLanguage ||
    !siteMetadata.siteDescription ||
    !siteMetadata.siteLogo
  ) {
    throw new Error('SEO component is missing required site metadata fields.')
  }

  const siteTitle = siteMetadata.siteTitle
  const siteUrl = siteMetadata.siteUrl.replace(/\/$/, '')

  const siteLogo = siteMetadata.siteLogo?.localFile?.publicURL || '/logo.png'

  const canonicalLink =
    canonical || (pathname && siteUrl + pathname) || siteMetadata.canonical
  const pageTitle = title?.trim() ? `${title.trim()} | ${siteTitle}` : siteTitle
  const language = lang || siteMetadata.siteLanguage
  const pageDescription = description || siteMetadata.siteDescription
  const bannerImage =
    metaImage && metaImage.src?.localFile?.publicURL
      ? `${siteUrl}${metaImage.src.localFile.publicURL}`
      : fallbackImage && fallbackImage.src?.localFile?.publicURL
        ? `${siteUrl}${fallbackImage.src.localFile.publicURL}`
        : `${siteUrl}${siteLogo}`
  const imgWidth = metaImage?.width || 875
  const imgHeight = metaImage?.height || 554

  const contacts = siteMetadata.contact || []

  const contactPoints = uniqueByKey(
    contacts.map((contact) => ({
      '@type': 'ContactPoint',
      contactType: contact?.contactType || '',
      telephone: contact?.telephone || '',
      email: contact?.email || '',
      areaServed: contact?.addressCountry || ''
    })),
    (item) =>
      `${item.contactType}|${item.telephone}|${item.email}|${item.areaServed}`
  )
  const addresses = uniqueByKey(
    contacts.map((contact) => ({
      '@type': 'PostalAddress',
      streetAddress: contact?.streetAddress || '',
      addressLocality: contact?.addressLocality || '',
      postalCode: contact?.postalCode || '',
      addressCountry: contact?.addressCountry || ''
    })),
    (item) =>
      `${item.streetAddress}|${item.addressLocality}|${item.postalCode}|${item.addressCountry}`
  )

  const baseSchema = [
    {
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: siteMetadata.siteTitle,
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}${siteLogo}`
      },
      ...(siteMetadata.socials &&
        siteMetadata.socials.length > 0 && {
          sameAs: siteMetadata.socials
            .filter((social) => social?.link)
            .map((social) => social.link)
        }),
      ...(contacts.length && {
        contactPoint: contactPoints,
        address: addresses
      })
    },
    {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: siteTitle,
      description: siteMetadata.siteDescription,
      publisher: {
        '@id': `${siteUrl}/#organization`
      },
      inLanguage: language
    },
    {
      '@type': 'WebPage',
      '@id': canonicalLink
        ? `${canonicalLink.replace(/\/$/, '')}/#webpage`
        : `${siteUrl}/#webpage`,
      url: canonicalLink || siteUrl,
      name: pageTitle,
      description: pageDescription,
      inLanguage: language,
      isPartOf: {
        '@id': `${siteUrl}/#website`
      },
      about: {
        '@id': `${siteUrl}/#organization`
      }
    }
  ]

  const schemaOrgWebPage = {
    '@context': 'https://schema.org',
    '@graph': [...baseSchema]
  }

  return (
    <>
      <html lang={language} />

      {/* General tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="image" content={bannerImage} />
      {canonicalLink && <link rel="canonical" href={canonicalLink} />}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-video-preview:-1, max-image-preview:large"
        />
      )}

      {/* OpenGraph tags */}
      <meta property="og:locale" content={language} />
      <meta property="og:type" content="website" />
      {canonicalLink && <meta property="og:url" content={canonicalLink} />}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:logo" content={`${siteUrl}${siteLogo}`} />
      <meta property="og:image" content={bannerImage} />
      <meta property="og:image:secure_url" content={bannerImage} />
      <meta property="og:image:width" content={String(imgWidth)} />
      <meta property="og:image:height" content={String(imgHeight)} />
      <meta property="og:image:alt" content={pageTitle} />
      <meta property="og:image:type" content="image/png" />

      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      {siteMetadata.twitterUsername && (
        <meta name="twitter:creator" content={siteMetadata.twitterUsername} />
      )}
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={bannerImage} />

      <script type="application/ld+json">
        {JSON.stringify(schemaOrgWebPage)}
      </script>
    </>
  )
}

export default SEO
